import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ProgrammeItem } from '@brent/shared/types'
import { useAuth } from '@/contexts/AuthContext'
import { useDeleteProgrammeItem, useReorderProgramme } from '@/hooks/useConcerts'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@brent/core-ui/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowUp, ArrowDown, Trash2, Check, X } from 'lucide-react'

interface ProgrammeListProps {
  concertId: number
  programme: ProgrammeItem[]
}

function MatchIndicator({ qid }: { qid: string | null }) {
  if (qid) {
    return <Check className="h-3.5 w-3.5 text-green-600" />
  }
  return <X className="h-3.5 w-3.5 text-muted-foreground/50" />
}

export function ProgrammeList({ concertId, programme }: ProgrammeListProps) {
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const deleteProgrammeItem = useDeleteProgrammeItem()
  const reorderProgramme = useReorderProgramme()
  const { toast } = useToast()
  const [deleteTarget, setDeleteTarget] = useState<ProgrammeItem | null>(null)

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deleteProgrammeItem.mutateAsync({
        concertId,
        itemId: deleteTarget.id,
      })
      toast({
        title: 'Removed',
        description: `Removed "${deleteTarget.work_title || 'item'}" from programme`,
      })
      setDeleteTarget(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to remove programme item',
      })
    }
  }, [deleteTarget, concertId, deleteProgrammeItem, toast])

  const handleMove = useCallback(
    async (itemId: number, direction: 'up' | 'down') => {
      const sorted = [...programme].sort((a, b) => a.position - b.position)
      const ids = sorted.map((item) => item.id)
      const idx = ids.indexOf(itemId)
      if (idx < 0) return

      const targetIdx = direction === 'up' ? idx - 1 : idx + 1
      if (targetIdx < 0 || targetIdx >= ids.length) return // Swap
      ;[ids[idx], ids[targetIdx]] = [ids[targetIdx], ids[idx]]

      try {
        await reorderProgramme.mutateAsync({ concertId, itemIds: ids })
      } catch (err) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to reorder programme',
        })
      }
    },
    [programme, concertId, reorderProgramme, toast],
  )

  const sorted = [...programme].sort((a, b) => a.position - b.position)
  const isReordering = reorderProgramme.isPending
  const isDeleting = deleteProgrammeItem.isPending

  return (
    <>
      {sorted.length > 0 ? (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead>Composer</TableHead>
                <TableHead>Work</TableHead>
                <TableHead>Notes</TableHead>
                {canEdit && <TableHead className="w-[120px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((item, idx) => (
                <TableRow key={item.id}>
                  <TableCell className="text-sm tabular-nums text-muted-foreground">
                    {item.position}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <MatchIndicator qid={item.composer_qid} />
                      {item.composer_id ? (
                        <button
                          type="button"
                          className="text-sm hover:underline text-left"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/composers/${item.composer_id}`)
                          }}
                        >
                          {item.composer_name || 'Unknown'}
                        </button>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {item.composer_name || '-'}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <MatchIndicator qid={item.work_qid} />
                      {item.work_id ? (
                        <button
                          type="button"
                          className="text-sm hover:underline text-left"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/works/${item.work_id}`)
                          }}
                        >
                          {item.work_title || 'Unknown'}
                        </button>
                      ) : (
                        <span className="text-sm">{item.work_title || '-'}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {[item.arrangement_note, item.notes].filter(Boolean).join(' — ') || '-'}
                    </span>
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMove(item.id, 'up')
                          }}
                          disabled={idx === 0 || isReordering}
                          title="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMove(item.id, 'down')
                          }}
                          disabled={idx === sorted.length - 1 || isReordering}
                          title="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteTarget(item)
                          }}
                          disabled={isDeleting}
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No programme items.</p>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove programme item?</DialogTitle>
            <DialogDescription>
              This will remove "{deleteTarget?.work_title || 'this item'}"
              {deleteTarget?.composer_name ? ` by ${deleteTarget.composer_name}` : ''} from the
              programme. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
